import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { AdminAppShell } from "../../../design-system/shells/AdminAppShell";
import { useAuth } from "../../../lib/auth";
import { api } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import StatusChip from "../../../components/StatusChip";
import { STATUS_FILTERS, errMsg } from "../../../lib/portfolio";

export default function PortfolioList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const founder = user?.role_key === "OWNER_SUPER_ADMIN";

  useEffect(() => {
    const params = filter === "archived" ? { status: "archived" } : filter ? { status: filter } : {};
    api.get("/admin/portfolio/projects", { params })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(errMsg(e, "Could not load projects.")));
  }, [filter]);

  const items = data?.items || [];
  return (
    <AdminAppShell activeNav="portfolio" title="Portfolio Manager" user={user}
      subtitle="Completed projects — upload, review, and publish to the public website."
      breadcrumbs={[{ label: "Owner Command Center", to: "/owner" }, { label: "Portfolio Manager", to: null }]}>
      <div className="flex flex-wrap items-center gap-2 mb-5" role="group" aria-label="Filter by status" data-testid="portfolio-status-filters">
        {STATUS_FILTERS.map((f) => (
          <Button key={f.key || "all"} variant={filter === f.key ? "default" : "outline"} aria-pressed={filter === f.key}
            className={filter === f.key ? "bg-erf-dark text-[#FEFCF7]" : "border-erf-borderCream text-erf-dark bg-white"}
            onClick={() => setFilter(f.key)} data-testid={`portfolio-filter-${f.key || "all"}`}>{f.label}</Button>
        ))}
        {founder && (
          <Button className="ml-auto bg-erf-gold text-erf-dark hover:bg-erf-goldHover" onClick={() => navigate("/admin/portfolio/new")}
            data-testid="portfolio-new-project-btn"><Plus className="w-4 h-4 mr-1.5" aria-hidden="true" />New project</Button>
        )}
      </div>
      {error && <p className="text-sm font-nav text-[#991B1B]" role="alert" data-testid="portfolio-list-error">{error}</p>}
      {data && items.length === 0 && (
        <Card className="ds-card border-erf-borderCream" data-testid="portfolio-empty">
          <CardContent className="p-6">
            <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">No projects here yet.</h2>
            <p className="text-sm font-nav text-erf-dark/80 mt-1">
              {founder ? "Create a project, then add photos and videos of the finished work." : "Projects you're invited to review will appear here."}
            </p>
          </CardContent>
        </Card>
      )}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="portfolio-project-grid">
        {items.map((p) => (
          <Link key={p.id} to={data.role === "founder" ? `/admin/portfolio/${p.id}` : `/admin/portfolio/${p.id}/review`}
            className="ds-card block rounded-xl border border-erf-borderCream bg-[#FEFCF7] p-4 transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-erf-gold"
            data-testid={`portfolio-project-card-${p.id}`}>
            <div className="ds-card-header">
              <div className="ds-card-header__title">
                <div className="font-display font-semibold text-erf-dark">{p.title}</div>
                <div className="text-xs font-nav text-erf-dark/70 ds-text-safe">
                  {[p.address_city, p.address_state].filter(Boolean).join(", ") || "No location"} · {p.media_count} file{p.media_count === 1 ? "" : "s"}
                </div>
              </div>
              <div className="ds-card-header__chip"><StatusChip status={p.status} testId={`portfolio-project-status-${p.id}`} /></div>
            </div>
            {p.summary && <p className="mt-2 text-sm font-nav text-erf-dark/80 line-clamp-2">{p.summary}</p>}
          </Link>
        ))}
      </div>
    </AdminAppShell>
  );
}
